/**
 * Which pages mean anything to an account that belongs to no agency.
 *
 * A superadmin legitimately has no `agency_id` — the role is cross-agency, and
 * the platform owner is not a customer of themselves. But `requireAgency` sent
 * any such account to /onboarding, so the one person who runs the platform was
 * pushed to create an agency in order to reach the page that administers all
 * of them.
 *
 * Letting them past that guard is only half an answer. Nearly every screen in
 * here queries by `agency_id`, and with none to query by they do not fail —
 * they come back empty. A complete, confident, empty book is the worst
 * outcome available: indistinguishable from a real agency with no data.
 *
 * So the pages below are the ones that work without an agency, and everything
 * else says plainly that this account has none.
 */

/**
 * Paths that are platform-wide or user-scoped rather than agency-scoped.
 *
 * Matched as a path or a prefix of one, so /admin covers /admin/data-health.
 * `/settings` is deliberately absent: it mixes account preferences with
 * agency configuration, and half a settings page is worse than a clear no.
 */
const PLATFORM_WIDE = [
  '/admin',
  '/pending-customers',
  '/notifications',
  '/help',
]

/** True when this path does not need the signed-in account to have an agency. */
export function isPlatformWidePath(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '') || '/'
  return PLATFORM_WIDE.some(base => path === base || path.startsWith(`${base}/`))
}

/**
 * Whether to show the page, or explain that there is no agency behind it.
 *
 * Takes the raw `agency_id` so a missing field, an empty string and a real id
 * are all handled in one place rather than at every call site.
 */
export function showsAgencyData(agencyId: unknown, pathname: string): boolean {
  if (agencyId) return true
  return isPlatformWidePath(pathname)
}
